/**
 * Product verticals (FLAGS) — "umbrella" features that an organization
 * turns on or off as a block while rolling out Space Academy progressively:
 * the stable core boots enabled and each polished vertical gets enabled per
 * academy when it's ready.
 *
 * Each vertical declares its consequences (`implications`) and its place in
 * the dependency graph (`requires`). Data is ALWAYS preserved when turning
 * off: new writes are blocked and the UI is hidden, nothing is deleted.
 *
 * These features travel through the TS manifest (`pnpm gen:features`) to the
 * Rust catalog and get reconciled as togglable `feature_flags` rows at boot.
 */

import type { FeatureManifest } from "./country/packs";

const BILLING_CORE: FeatureManifest = {
  key: "billing.core",
  name: "Caja y facturación",
  description:
    "Vertical de cobros de matrícula: facturas y cuotas, registro de pagos (efectivo, pago móvil, transferencia, Zelle, Stripe), verificación, tasas del día y recibos.",
  scope: "org",
  status: "stable",
  gateable: true,
  since: "2026-06",
  docsSlug: "caja",
  implications: {
    navHidden: ["Caja (/finance)", "Mis pagos (/payments)"],
    flowsBlocked: [
      "Registrar y verificar pagos",
      "Emitir facturas y planes de pago",
      "Checkout online (Stripe)",
      "Actualizar la tasa del día",
    ],
    dataPolicy: "preserved",
  },
};

/**
 * B4 · G1/G3/G4 — «Adelanta y ahorra» (Pay ahead and save).
 *
 * Owner's business rule: the student took and paid for the first month, and
 * is then offered the chance to pay the remaining months in advance with a
 * discount percentage. The stated motive is cash flow, so the metric that
 * justifies it (collected in advance vs projected) goes in the same vertical.
 *
 * Turning it off does NOT touch any prepayment already issued: an invoice is
 * money and the data is always preserved — the offer closes, not the ledger.
 */
const BILLING_PREPAY: FeatureManifest = {
  key: "billing.prepay",
  name: "Prepago con descuento",
  description:
    "Oferta «Adelanta y ahorra»: el alumno paga por adelantado las cuotas que le quedan con un descuento escalado, y la academia ve el cuadro de caja (cobrado por adelantado vs proyectado).",
  scope: "org",
  status: "beta",
  gateable: true,
  since: "2026-07",
  docsSlug: "caja",
  // SOFT edge over the billing vertical: prepay composes with it, but turning
  // off billing entirely cannot be held hostage to this offer.
  requires: ["billing.core?"],
  implications: {
    navHidden: ["Tarjeta «Adelanta y ahorra» en /payments y en el curso"],
    flowsBlocked: [
      "Ofrecer y aceptar el prepago con descuento",
      "Cuadro de caja de cobros adelantados",
    ],
    dataPolicy: "preserved",
  },
};

const CRM_LEADS: FeatureManifest = {
  key: "crm.leads",
  name: "CRM y captación",
  description:
    "Vertical de captación: leads con UTM, pipeline kanban, timeline de contactos, conversión a alumno y formulario público de interés.",
  scope: "org",
  status: "stable",
  gateable: true,
  since: "2026-06",
  docsSlug: "captacion",
  implications: {
    navHidden: ["CRM (/crm)"],
    flowsBlocked: [
      "Crear y editar leads",
      "Registrar eventos de contacto",
      "Convertir leads en alumnos",
      "Formulario público de interés",
    ],
    dataPolicy: "preserved",
  },
};

const CRM_CAMPAIGNS: FeatureManifest = {
  key: "crm.campaigns",
  name: "Campañas segmentadas",
  description:
    "Campañas de email dirigidas a segmentos del CRM (deuda, inactividad, interés). Requiere el CRM y las campañas de email.",
  scope: "org",
  status: "stable",
  gateable: true,
  since: "2026-06",
  docsSlug: "captacion",
  requires: ["crm.leads", "email.campaigns"],
  implications: {
    navHidden: ["Segmentos y campañas dentro del CRM"],
    flowsBlocked: ["Crear segmentos", "Programar/enviar campañas segmentadas"],
    dataPolicy: "preserved",
  },
};

const EMAIL_CAMPAIGNS: FeatureManifest = {
  key: "email.campaigns",
  name: "Campañas de email",
  description:
    "Envío de campañas de email desde el back-office (plantillas, programación, fan-out por destinatarios).",
  scope: "platform",
  status: "stable",
  gateable: true,
  since: "2026-06",
  docsSlug: "administrador",
  implications: {
    navHidden: ["Email marketing en el admin"],
    flowsBlocked: ["Crear, programar y enviar campañas de email"],
    dataPolicy: "preserved",
  },
};

const GAMIFICATION_CORE: FeatureManifest = {
  key: "gamification.core",
  name: "Gamificación (XP, rachas, ligas)",
  description:
    "Capa de motivación: XP por lecciones, rachas diarias, ligas y tabla de posiciones. El progreso académico NO depende de ella.",
  scope: "org",
  status: "stable",
  since: "2026-06",
  docsSlug: "alumno",
  implications: {
    navHidden: ["Ligas (/leaderboard)"],
    flowsBlocked: [],
    dataPolicy: "preserved",
  },
};

const GAMIFICATION_BADGES: FeatureManifest = {
  key: "gamification.badges",
  name: "Insignias",
  description:
    "Insignias otorgadas por hitos (participación en foros, rachas, logros de cohorte). Requiere la gamificación base.",
  scope: "org",
  status: "stable",
  gateable: true,
  since: "2026-06",
  docsSlug: "alumno",
  requires: ["gamification.core"],
  implications: {
    navHidden: ["Vitrina de insignias del perfil"],
    flowsBlocked: ["Otorgar insignias nuevas"],
    dataPolicy: "preserved",
  },
};

const MEDIA_RECORDING: FeatureManifest = {
  key: "media.recording",
  name: "Grabaciones de clases (VOD)",
  description:
    "Enlazar la grabación de una clase en vivo como vídeo re-visitable por la cohorte con el player propio. Requiere el bloque de vídeo y las clases en vivo.",
  scope: "org",
  status: "stable",
  gateable: true,
  since: "2026-06",
  docsSlug: "profesor",
  requires: ["media.block.video", "academy.module.live_session"],
  implications: {
    navHidden: ["Sección «Grabación» de la sesión en vivo"],
    flowsBlocked: ["Adjuntar grabaciones a sesiones"],
    dataPolicy: "preserved",
  },
};

const FLIPPED_SESSIONS: FeatureManifest = {
  key: "academy.flipped_sessions",
  name: "Aula invertida (plan de clase)",
  description:
    "Organiza los módulos de la cohorte alrededor de cada clase en vivo con una fase (antes/durante/después): el profesor arma el plan de clase y el alumno ve una ruta semana a semana con sus tareas marcadas.",
  scope: "org",
  status: "stable",
  gateable: true,
  since: "2026-07",
  docsSlug: "alumno",
  // SOFT dependency on the live-class module kind: the plan composes better
  // with it (the "during" phase links the session), but isn't held hostage to
  // it — the live class is the schedule's own class_session (core), so
  // turning off the kind doesn't invalidate the class plan.
  requires: ["academy.module.live_session?"],
  implications: {
    navHidden: [],
    flowsBlocked: [
      "Armar el plan de clase de una sesión (antes/durante/después)",
      "Ver la ruta de aula invertida de una clase",
    ],
    dataPolicy: "preserved",
  },
};

const TUTORING: FeatureManifest = {
  key: "academy.tutoring",
  name: "Tutorías (corrección y reentrega)",
  description:
    "Rol de tutor con cola de corrección y SLA, y ciclo entrega → feedback → reentrega con historial de versiones. El tutor corrige y devuelve entregas de sus cohortes; el alumno reentrega hasta aprobar.",
  scope: "org",
  status: "stable",
  gateable: true,
  since: "2026-07",
  docsSlug: "alumno",
  // SOFT dependency on the assignment kind: the resubmission cycle operates
  // on assignment submissions, but isn't held hostage if the kind is turned off.
  requires: ["academy.module.assignment?"],
  implications: {
    navHidden: ["Tutorías"],
    flowsBlocked: [
      "Panel de tutorías (cola de corrección con SLA)",
      "Reentregar un desafío tras la devolución del tutor",
    ],
    dataPolicy: "preserved",
  },
};

/**
 * **DISABLED FOR V1** (owner's decision, 2026-08-14). The tutor sends the
 * student's literal text to an external provider and there is no consent flow
 * set up; in an academy that student can be a minor. This only fixes the
 * factory DEFAULT —it used to start on, unlike the other four AI
 * capabilities—; the real cutoff, which no level can lift, lives in
 * `./ai-release` and in `core-rust/src/ai/release.rs`.
 */
const AI_TUTOR: FeatureManifest = {
  key: "academy.ai_tutor",
  name: "Tutor IA 24/7 (desactivado en V1)",
  description:
    "Tutor con IA que responde preguntas del alumno con el material del curso y da feedback PRELIMINAR de entregas antes de la corrección humana (borrador de la IA, no es la nota). Motor configurado por organización vía la integración de IA. Con cuota mensual de tokens por academia. NO SE PUBLICA EN V1: el chat del alumno viaja a un proveedor externo y el consentimiento (del representante, cuando el alumno es menor) todavía no existe — ver `academy/ai-release.ts`.",
  scope: "org",
  status: "beta",
  gateable: true,
  // Turning on an AI that also hands student text to a third party is a
  // decision, not an inheritance. The other four AI capabilities already started this way.
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "tutor-ia",
  // The dependency on the AI integration is a CONFIG one (display-only): it is
  // not an edge of the flag graph (integrations are not nodes of the TS
  // graph). The runtime gate checks that the credential is configured — if
  // not, the endpoint responds "unavailable" (503) instead of 403.
  implications: {
    navHidden: [
      "Tutor IA (/ai-tutor)",
      "Botón «Pregúntale al tutor»",
      "Botón «Pedir feedback de la IA»",
    ],
    flowsBlocked: [
      "Chatear con el tutor IA",
      "Pedir feedback preliminar de la IA sobre una entrega",
    ],
    dataPolicy: "preserved",
  },
};

/**
 * B8 · COMMERCIAL chatbot for the academy. It's a different consumer, not the
 * tutor: that one responds with course material to an enrolled student; this
 * one answers prices and schedules to people **without an account**.
 *
 * Starts off, like the four authoring ones: turning on an AI that costs money
 * is a decision made by the owner, not an inherited default.
 */
const AI_CHATBOT: FeatureManifest = {
  key: "academy.ai_chatbot",
  name: "Asistente de la academia (leads)",
  description:
    "Chatbot público que responde precios, horarios, modalidades, formas de pago, requisitos y certificados a personas SIN cuenta, citando la FAQ de la academia y el catálogo en vivo. Solo INFORMA: no pre-matricula, no genera enlaces de pago y no agenda. Cuando la confianza baja o lo pide el interesado, escala a WhatsApp y crea un lead en el CRM con la transcripción.",
  scope: "org",
  status: "beta",
  gateable: true,
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "asistente-academia",
  // Soft: without CRM the chatbot still responds and offers WhatsApp; what's
  // lost is the lead with the transcript. Not a reason to block.
  requires: ["crm.leads?"],
  implications: {
    navHidden: [
      "Asistente de la academia (widget público)",
      "Panel de deflexión (/admin/academy-bot)",
    ],
    flowsBlocked: [
      "Preguntar al asistente sin tener cuenta",
      "Escalar una conversación a WhatsApp con lead automático",
    ],
    dataPolicy: "preserved",
  },
};

/** B8 · Generate the structure of a course and the content of its blocks. */
const AI_AUTHORING: FeatureManifest = {
  key: "academy.ai_authoring",
  name: "Autoría de cursos con IA",
  description:
    "Convierte un encargo en lenguaje natural («Marketing digital, nivel 1, 3 meses, 2 clases por semana») en una PROPUESTA de programa, curso, unidades, módulos y bloques. La IA nunca escribe en la base de datos: produce una propuesta que se valida contra los registros reales de bloques y de módulos, y que una persona aprueba pieza a pieza. Aplicarla usa las mismas mutaciones que el editor, así que actividad, papelera y deshacer funcionan igual.",
  scope: "org",
  status: "beta",
  gateable: true,
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "autoria-ia",
  implications: {
    navHidden: ["Generar con IA (editor de cursos)", "Propuestas de IA (/ai-authoring)"],
    flowsBlocked: [
      "Generar la estructura de un curso con IA",
      "Generar el contenido de los bloques de una lección con IA",
    ],
    dataPolicy: "preserved",
  },
};

/** B8 · Propose a diff on a course that ALREADY exists. */
const AI_AUTHORING_EDIT: FeatureManifest = {
  key: "academy.ai_authoring.edit",
  name: "Edición de cursos con IA",
  description:
    "Propone cambios sobre un curso existente como un diff pieza a pieza (añadir unidad, reescribir un bloque, reordenar módulos, retirar un recurso), con previsualización y aprobación o rechazo por partes. Razonar sobre lo ya construido sin romperlo es más difícil que crear desde cero: por eso lleva su propio interruptor.",
  scope: "org",
  status: "beta",
  gateable: true,
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "autoria-ia",
  requires: ["academy.ai_authoring"],
  implications: {
    navHidden: ["Proponer cambios con IA (editor de cursos)"],
    flowsBlocked: ["Pedir a la IA un diff sobre un curso existente"],
    dataPolicy: "preserved",
  },
};

/** B8 · Generate CONFIGURATIONS (rubrics, completion rules, class plan). */
const AI_AUTHORING_CONFIG: FeatureManifest = {
  key: "academy.ai_authoring.config",
  name: "Configuraciones generadas con IA",
  description:
    "Propone rúbricas, reglas de aprobación, esquemas de nota y plan de clase. Ninguna configuración generada evita el gate de su propia feature, y nada que toque la nota o el certificado se genera con un modelo barato: una rúbrica sutilmente mal hecha propaga a todas las notas de la cohorte y nadie la detecta leyéndola.",
  scope: "org",
  status: "experimental",
  gateable: true,
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "autoria-ia",
  requires: ["academy.ai_authoring"],
  implications: {
    navHidden: ["Generar rúbrica con IA", "Generar reglas de aprobación con IA"],
    flowsBlocked: ["Pedir a la IA una rúbrica, un esquema de nota o un plan de clase"],
    dataPolicy: "preserved",
  },
};

/**
 * B8 · Apply a proposal WITHOUT human approval.
 *
 * Exists so its absence is explicit and verifiable. **Stays off in v1** and
 * there is a test that verifies it: human review is the cheap half of the
 * hybrid rule, and removing it without saying so would be the most expensive
 * silent failure in this whole feature.
 */
const AI_AUTHORING_AUTOAPPLY: FeatureManifest = {
  key: "academy.ai_authoring.autoapply",
  name: "Aplicar propuestas de IA sin revisión (no usar en v1)",
  description:
    "Permitiría aplicar una propuesta de la IA sin que una persona la apruebe. APAGADA de fábrica y así se queda en la v1: la revisión humana es lo que hace aceptable generar contenido con un modelo barato. Existe declarada para que su ausencia sea una decisión visible y testeable, no un olvido.",
  scope: "org",
  status: "experimental",
  // Not gateable: it doesn't gate a write, but the ABSENCE of a review. Its
  // guarantee is the test that checks it still resolves off.
  gateable: false,
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "autoria-ia",
  requires: ["academy.ai_authoring"],
};

const COMPLETION_RULES: FeatureManifest = {
  key: "academy.completion_rules",
  name: "Reglas de aprobación",
  description:
    "Motor de criterios configurables que decide quién APRUEBA una cohorte y recibe certificado al cerrarla: asistencia mínima, desafíos aprobados, proyecto final, nota mínima y marca del instructor (combinables con Y/O). Sin reglas, se conserva la puerta de nota heredada.",
  scope: "course",
  status: "beta",
  gateable: true,
  since: "2026-07",
  docsSlug: "administrador",
  implications: {
    navHidden: ["Editor de reglas de aprobación (ajustes de curso/cohorte)"],
    flowsBlocked: [
      "Editar las reglas de aprobación de un curso/cohorte",
      "Marcar manualmente a un alumno como completo",
      "Auto-certificar en el cierre según reglas (sin la feature se usa la puerta de nota)",
    ],
    dataPolicy: "preserved",
  },
};

const FINAL_PROJECT: FeatureManifest = {
  key: "academy.final_project",
  name: "Proyecto final",
  description:
    "Formaliza el proyecto final como hito de la cohorte: la academia DESIGNA un trabajo como EL proyecto final del curso, publica su rúbrica para que el alumno la vea ANTES de entregar (criterios, niveles y puntos), fija una fecha límite por cohorte y, al cerrar, solo certifica a quien lo aprueba. Corrección de un solo intento opcional (estilo CoderHouse).",
  scope: "course",
  status: "beta",
  gateable: true,
  since: "2026-07",
  docsSlug: "alumno",
  // SOFT dependencies: the final project composes with the completion rules
  // (it contributes the `final_project` criterion) and with the assessment
  // module (the project IS an assignment), but isn't held hostage to either —
  // turning off the requirement doesn't force turning off the final project.
  requires: ["academy.completion_rules?", "academy.module.assignment?"],
  implications: {
    navHidden: ["Proyecto final (/final-project)"],
    flowsBlocked: [
      "Designar un assignment como el proyecto final del curso",
      "Publicar la rúbrica del proyecto final al alumno",
      "Entregar el proyecto final",
    ],
    // Data is ALWAYS preserved: submissions and grades remain; only new writes
    // are blocked and the UI is hidden.
    dataPolicy: "preserved",
  },
};

const REST_ORG_CATALOG: FeatureManifest = {
  key: "academy.data.rest_org_catalog",
  name: "Catálogo de la academia por API",
  description:
    "El catálogo interno de la academia (qué cursos ofrece, su árbol de contenido y publicar/despublicar) se pide al servidor en vez de replicarse al dispositivo. Cierra la fuga por la que TODO miembro —incluido un alumno— recibía offline el árbol completo de todos los cursos de la academia, borradores y cursos borrados incluidos. El alumno conserva offline el curso en el que está matriculado.",
  scope: "org",
  status: "beta",
  gateable: true,
  since: "2026-07",
  docsSlug: "administrador",
  implications: {
    navHidden: [],
    flowsBlocked: [
      "Publicar un curso en la academia desde la API",
      "Despublicar un curso de la academia desde la API",
    ],
    dataPolicy: "preserved",
  },
};

/**
 * §L1 · IN-PERSON modality — the only vertical that starts OFF.
 *
 * The academy is online-only by business decision: «the platform must only
 * offer online courses, no exceptions». The in-person side is NOT deleted —
 * campuses, classrooms, occupancy, QR check-in and the `in_person`/`hybrid`
 * modalities remain implemented, with their tests running and their data
 * intact (`dataPolicy: preserved`) — but the platform doesn't offer them
 * until the academy grows and someone turns the flag on on purpose.
 *
 * `defaultEnabled: false` is what sets it apart from the rest: with no row in
 * `feature_flags` and no override, it resolves OFF (the rest resolve on).
 */
const MODALITY_IN_PERSON: FeatureManifest = {
  key: "academy.modality.in_person",
  name: "Modalidad presencial (sedes, aulas y pase de lista)",
  description:
    "Clases con asistencia física: sedes y aulas de la academia, mapa de ocupación, pase de lista con código QR y las modalidades «presencial» e «híbrida» de una cohorte. Construida y probada, pero APAGADA de fábrica: esta academia es 100 % online. Encenderla no crea nada nuevo — devuelve a la vista lo que ya existe.",
  scope: "org",
  status: "stable",
  gateable: true,
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "administrador",
  implications: {
    navHidden: ["Sedes (/academy/campuses)", "Pase de lista (/checkin)"],
    flowsBlocked: [
      "Crear y editar sedes y aulas",
      "Abrir el pase de lista con código QR de una clase",
      "Registrar asistencia presencial con el código de la clase",
      "Elegir modalidad «presencial» o «híbrida» al crear o editar una cohorte",
    ],
    dataPolicy: "preserved",
  },
};

/**
 * B6 · LEVELS — the umbrella. Paths, stages and student state.
 *
 * Starts OFF: publishing a path changes what the student CAN open, and that
 * cannot turn on by itself. The backfill leaves every path as a draft for
 * exactly that same reason.
 */
const PROGRESSION: FeatureManifest = {
  key: "academy.progression",
  name: "Rutas de progresión (niveles)",
  description:
    "Convierte el plan de estudios en una ruta de niveles con desbloqueo configurable. Por defecto un nivel es un curso dentro de un programa, pero la academia puede elegir otro origen —un programa entero, una unidad, o niveles definidos a mano— sin tocar código.",
  scope: "org",
  status: "beta",
  gateable: true,
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "alumno",
  implications: {
    navHidden: ["Mi ruta (/path)"],
    flowsBlocked: [
      "Crear y publicar rutas de progresión",
      "Reconciliar las etapas de una ruta con su programa",
      "Editar el desbloqueo o las recompensas de una etapa",
    ],
    dataPolicy: "preserved",
  },
};

// ─── B2 · CLASSROOM COMMUNICATION (plan docs/plans/02-chat-aula.md) ───────────
//
// Five flags, one per stage, because they have different COST profiles and
// the owner asked to be able to turn off each part separately in the face of
// a billing problem. The graph order goes from free to expensive:
//
//   roster (REST, ~0)  →  chat (per-cohort replica)  →  promote (~0)
//                                                     →  mentions (0 rows)
//                                                     →  reactions (+20% of
//                                                        submissions ⇒ starts OFF)
//
// WARNING that applies to all five: turning off a flag does NOT trim a stream
// (explicit decision in `platform/features.rs`: trimming streams per feature
// breaks checkpoints and offline caches). The chat's COST kill-switch is the
// retention window (`CHAT_RETENTION_DAYS`); the flag turns off WRITES and the UI.

const CLASSROOM_ROSTER: FeatureManifest = {
  key: "academy.classroom.roster",
  name: "Compañeros del aula",
  description:
    "El alumno ve quién más está en su cohorte: nombre para mostrar y avatar, nada más. NUNCA correo, cédula, teléfono, estado de matrícula ni notas — eso sigue siendo expediente y solo lo ve quien gestiona. Se sirve por API (`GET /api/cohorts/:id/roster`), no por réplica: identidad de terceros no viaja al dispositivo.",
  scope: "org",
  status: "beta",
  gateable: true,
  since: "2026-07",
  docsSlug: "alumno",
  implications: {
    navHidden: ["Compañeros (dentro del aula)"],
    flowsBlocked: [
      "Ver la lista de compañeros de la cohorte",
      "Ver el nombre y el avatar de quien escribe en el chat del aula",
    ],
    dataPolicy: "preserved",
  },
};

/**
 * **Only the visual path.** The engine keeps computing: turning it off
 * returns `/learn` to the usual list without losing state or certificates.
 *
 * Not `gateable`: it doesn't block any write, it decides whether the client
 * draws the map. Inventing a gate for it on a write-path that doesn't exist
 * would be lying to the `GATED_KEYS` completeness test.
 */
const PROGRESSION_PATH: FeatureManifest = {
  key: "academy.progression.path",
  name: "Camino visual de la ruta",
  description:
    "Mapa de niveles en serpiente con nodos por clase o módulo e hitos de fin de mes, estados con icono y texto (nunca solo color) y explicación de por qué un nivel está cerrado.",
  scope: "org",
  status: "beta",
  since: "2026-07",
  docsSlug: "alumno",
  requires: ["academy.progression"],
};

/**
 * **Only the gating.** Turned off, every stage stays available and the
 * `unlock_config` is kept without being evaluated: turning off doesn't delete a single rule.
 */
const PROGRESSION_PREREQUISITES: FeatureManifest = {
  key: "academy.progression.prerequisites",
  name: "Prerrequisitos entre niveles",
  description:
    "Aplica el árbol de desbloqueo de cada nivel. Apagada, todos los niveles quedan disponibles y la configuración se conserva intacta para cuando se vuelva a encender.",
  scope: "org",
  status: "beta",
  since: "2026-07",
  docsSlug: "alumno",
  requires: ["academy.progression"],
};

/**
 * LEVEL SKIPPING is an **exception, not the norm**: it starts off and the
 * academy enables it when it decides one can enter without taking the prior level.
 */
const PROGRESSION_PLACEMENT: FeatureManifest = {
  key: "academy.progression.placement",
  name: "Prueba de nivelación (salto de nivel)",
  description:
    "Permite entrar a un nivel sin haber cursado el anterior aprobando una prueba. La nota la pone el servidor al corregir. Nace apagada: saltarse un nivel es la excepción, no el camino.",
  scope: "org",
  status: "beta",
  defaultEnabled: false,
  since: "2026-07",
  gateable: true,
  docsSlug: "alumno",
  requires: ["academy.progression.prerequisites"],
  implications: {
    navHidden: [],
    flowsBlocked: ["Registrar el resultado de una prueba de nivelación"],
    dataPolicy: "preserved",
  },
};

const CLASSROOM_CHAT: FeatureManifest = {
  key: "academy.classroom.chat",
  name: "Chat del aula",
  description:
    "Canal plano por cohorte con marca de leído, moderación del profesorado (ocultar un mensaje lo saca del dispositivo del alumno) y VENTANA DE RETENCIÓN: pasados los días configurados, los mensajes salen de la réplica sin borrarse. Convive con el foro, que sigue guardando lo que merece hilo permanente.",
  scope: "org",
  status: "beta",
  gateable: true,
  since: "2026-07",
  docsSlug: "alumno",
  // HARD: a chat without names is unusable and its moderation, unenforceable.
  requires: ["academy.classroom.roster"],
  implications: {
    navHidden: ["Chat (pestaña del aula)"],
    flowsBlocked: [
      "Enviar mensajes en el chat del aula",
      "Moderar el chat del aula (ocultar y restaurar mensajes)",
    ],
    dataPolicy: "preserved",
  },
};

const CLASSROOM_CHAT_PROMOTE: FeatureManifest = {
  key: "academy.classroom.chat.promote",
  name: "Destacar y promover mensajes del chat",
  description:
    "El profesorado fija mensajes (quedan exentos del archivado) y promueve un mensaje a hilo del foro de la cohorte. Es lo que hace legítimo vaciar el chat: lo que valía ya está en el foro.",
  scope: "org",
  status: "beta",
  gateable: true,
  since: "2026-07",
  docsSlug: "alumno",
  requires: ["academy.classroom.chat"],
  implications: {
    navHidden: [],
    flowsBlocked: [
      "Fijar un mensaje del chat del aula",
      "Promover un mensaje del chat a hilo del foro",
    ],
    dataPolicy: "preserved",
  },
};

const CLASSROOM_CHAT_MENTIONS: FeatureManifest = {
  key: "academy.classroom.chat.mentions",
  name: "Menciones en el chat del aula",
  description:
    "Escribir @nombre avisa a esa persona con una notificación inmediata en vez de esperar al resumen. No crea ni una fila nueva: las menciones se leen del propio texto del mensaje al hacer el fan-out.",
  scope: "org",
  status: "beta",
  gateable: true,
  since: "2026-07",
  docsSlug: "alumno",
  requires: ["academy.classroom.chat", "academy.classroom.roster"],
  implications: {
    navHidden: [],
    flowsBlocked: ["Avisar por mención (@nombre) en el chat del aula"],
    dataPolicy: "preserved",
  },
};

/**
 * `billing.core?` is deliberately a SOFT edge: turning off billing must not
 * hijack the offer, which degrades to «Continue with Level 2» with no price.
 */
const PROGRESSION_UPSELL: FeatureManifest = {
  key: "academy.progression.upsell",
  name: "Oferta del siguiente nivel",
  description:
    "Al completar un nivel, ofrece el siguiente con su precio y el ahorro por adelantar. La oferta persiste hasta su caducidad o hasta que el alumno la descarte: un descuento que solo se ve una vez no convierte. Sin caja encendida, la tarjeta se muestra sin precio.",
  scope: "org",
  status: "beta",
  defaultEnabled: false,
  since: "2026-07",
  gateable: true,
  docsSlug: "caja",
  requires: ["academy.progression", "billing.core?"],
  implications: {
    navHidden: [],
    flowsBlocked: ["Descartar la oferta del siguiente nivel"],
    dataPolicy: "preserved",
  },
};

const CLASSROOM_CHAT_REACTIONS: FeatureManifest = {
  key: "academy.classroom.chat.reactions",
  name: "Reacciones del chat del aula",
  description:
    "Reaccionar con uno de seis emojis (catálogo cerrado, una reacción por persona y mensaje). Nace APAGADA a propósito: es la operación más barata de escribir y la más cara de replicar (~+20 % de entregas de fila), así que es el primer recorte ante una factura inesperada y el único que no pierde nada funcional.",
  scope: "org",
  status: "beta",
  gateable: true,
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "alumno",
  requires: ["academy.classroom.chat"],
  implications: {
    navHidden: [],
    flowsBlocked: ["Reaccionar a un mensaje del chat del aula"],
    dataPolicy: "preserved",
  },
};

/** Vertical manifests (source `academy_verticals` of the catalog). */
/**
 * User profile photo (minors zero-rule in
 * `infra/AVATARS_IMPLEMENTATION.md` §0).
 *
 * `scope: "platform"` on purpose: the photo belongs to the USER, not to an
 * academy, and someone studying at two of them can't have half a photo. What
 * each academy DOES decide is from what age one of its STUDENTS can publish
 * it, and that isn't a flag but `org_settings.adult_age` — because it isn't a
 * product switch, it's a legal criterion with a default that protects (18).
 *
 * Turning it off does NOT delete any photo nor prevent removing your own: it
 * closes the button to set a new one and the upload endpoint. `dataPolicy: preserved`.
 */
const PROFILE_AVATAR: FeatureManifest = {
  key: "academy.profile.avatar",
  name: "Foto de perfil",
  description:
    "Cada persona puede subir su foto, cambiarla y quitarla; si no tiene, se pintan sus iniciales sobre un color propio y estable. La foto de un ALUMNO solo es visible para terceros si consta su fecha de nacimiento en el expediente y es mayor de la edad que fije la academia (`org_settings.adult_age`, 18 por defecto): sin fecha, no hay foto. Profesorado, personal y representantes comparten siempre.",
  scope: "platform",
  status: "beta",
  gateable: true,
  since: "2026-07",
  docsSlug: "alumno",
  implications: {
    navHidden: ["Cambiar foto (dentro de /profile)"],
    flowsBlocked: [
      "Subir o cambiar la foto de perfil",
      "Adoptar la foto de la cuenta de Google al entrar",
    ],
    dataPolicy: "preserved",
  },
};

export function verticalManifests(): FeatureManifest[] {
  return [
    PROGRESSION,
    PROGRESSION_PATH,
    PROGRESSION_PREREQUISITES,
    PROGRESSION_PLACEMENT,
    PROGRESSION_UPSELL,
    BILLING_CORE,
    BILLING_PREPAY,
    CRM_LEADS,
    CRM_CAMPAIGNS,
    EMAIL_CAMPAIGNS,
    GAMIFICATION_CORE,
    GAMIFICATION_BADGES,
    MEDIA_RECORDING,
    FLIPPED_SESSIONS,
    AI_TUTOR,
    // B8 · one flag per AI capability (the owner wants to cut by billing).
    AI_CHATBOT,
    AI_AUTHORING,
    AI_AUTHORING_EDIT,
    AI_AUTHORING_CONFIG,
    AI_AUTHORING_AUTOAPPLY,
    COMPLETION_RULES,
    FINAL_PROJECT,
    TUTORING,
    REST_ORG_CATALOG,
    MODALITY_IN_PERSON,
    // B2 · classroom communication (one entry per stage).
    CLASSROOM_ROSTER,
    CLASSROOM_CHAT,
    CLASSROOM_CHAT_PROMOTE,
    CLASSROOM_CHAT_MENTIONS,
    CLASSROOM_CHAT_REACTIONS,
    // Profile photo (minors zero-rule).
    PROFILE_AVATAR,
  ];
}

/**
 * B2 · Classroom communication keys, exported so nav, routes, hooks and
 * tests reference them **only once** (no loose literals that could drift
 * out of sync with the manifest).
 */
export const CLASSROOM_ROSTER_KEY = CLASSROOM_ROSTER.key;
export const CLASSROOM_CHAT_KEY = CLASSROOM_CHAT.key;
export const CLASSROOM_CHAT_PROMOTE_KEY = CLASSROOM_CHAT_PROMOTE.key;
export const CLASSROOM_CHAT_MENTIONS_KEY = CLASSROOM_CHAT_MENTIONS.key;
export const CLASSROOM_CHAT_REACTIONS_KEY = CLASSROOM_CHAT_REACTIONS.key;

/**
 * §L1 · Key of the in-person gate. Exported so nav, routes, the modality
 * selector and tests reference it **only once** (no loose literals that
 * could drift out of sync with the manifest).
 */
export const MODALITY_IN_PERSON_KEY = MODALITY_IN_PERSON.key;

/**
 * Key of the profile photo gate. Exported so the profile, the feature hook
 * and tests reference it **only once**: a loose literal in a `<Show>` is
 * exactly how a client gate drifts out of sync with the manifest.
 */
export const PROFILE_AVATAR_KEY = PROFILE_AVATAR.key;

/**
 * B8 · AI capability keys, exported so nav, routes, the course editor and
 * tests reference them **only once**. A loose literal in a
 * `<Show>` is exactly how a client gate drifts out of sync with
 * the manifest.
 */
export const AI_CHATBOT_KEY = AI_CHATBOT.key;
export const AI_AUTHORING_KEY = AI_AUTHORING.key;
export const AI_AUTHORING_EDIT_KEY = AI_AUTHORING_EDIT.key;
export const AI_AUTHORING_CONFIG_KEY = AI_AUTHORING_CONFIG.key;
export const AI_AUTHORING_AUTOAPPLY_KEY = AI_AUTHORING_AUTOAPPLY.key;

/**
 * §L2 · Factory modality for a new cohort. Live online: the academy is
 * online-only, so the no-decisions path leads to the right thing. Mirrors
 * the `DEFAULT 'online_live'` of `cohort.modality` (migration 00083).
 */
export const DEFAULT_COHORT_MODALITY = "online_live" as const;

/** Modalities that require physical presence (gated by the §L1 flag). */
export const IN_PERSON_MODALITIES: readonly string[] = ["in_person", "hybrid"];

/** Does this modality need the in-person vertical to be enabled? */
export function modalityNeedsInPerson(modality: string | null | undefined): boolean {
  return modality !== null && modality !== undefined && IN_PERSON_MODALITIES.includes(modality);
}
